import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'
import { DropdownStyleConfig } from '@/components/Tailwind/Dropdown'
import { TableStyleConfig } from '@/components/Tailwind/Table'
import { PaginatorStyleConfig } from '@/components/Tailwind/Paginator'
import { TabStyleConfig } from '@/components/Tailwind/Tabs'
import { CheckboxStyleConfig } from '@/components/Tailwind/Checkbox'
import { ModalStyleConfig } from '@/components/Tailwind/Modal'
import { InputNumberStyleConfig } from '@/components/Tailwind/InputStepper'
import { GalleriaStyleConfig } from '@/components/Tailwind/ImageCarousel'
import { CascadeDropdownSingleStyleConfig } from '@/components/Tailwind/CascadingDropdown'
import { ButtonStyleConfig } from '@/components/Tailwind/Button'
import { SelectButtonStyleConfig } from '@/components/Tailwind/SegmentedControl'

export const BackbarStylingPreset = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: 'var(--border-radius-sm)',
      md: 'var(--border-radius)',
      lg: '8px',
      xl: '12px'
    }
  },
  semantic: {
    iconSize: '14px',
    formField: {
      paddingX: 'var(--padding-sm)',
      paddingY: 'var(--padding-sm)',
      borderRadius: '{border.radius.sm}',
      placeholderColor: 'var(--color-text-subdued)'
    },
    disabledOpacity: '1',
    colorScheme: {
      light: {
        formField: {
          hoverColor: 'var(--color-surface-hover)',
          color: 'var(--color-text)',
          borderColor: 'var(--border-default-color)',
          hoverBorderColor: 'var(--border-hover-color)',
          focusBorderColor: 'var(--border-pressed-color)',
          placeholderColor: 'var(--color-text-subdued)',
          disabledColor: 'var(--color-text-disabled)',
          disabledBackground: 'var(--color-surface-neutral)',
          disabledBorderColor: 'var(--border-disabled-color)'
        },
        overlay: {
          select: {
            hoverColor: 'var(--color-surface-hover)',
            focusColor: 'var(--color-text)'
          }
        },
        list: {
          option: {
            color: 'var(--color-text)',
            focusColor: 'var(--color-text)',
            selectedColor: 'var(--color-text)',
            selectedFocusColor: 'var(--color-text)',
            focusBackground: 'var(--color-surface-hover)',
            selectedFocusBackground: 'var(--color-surface-hover)',
            selectedBackground: 'transparent'
          }
        },
        text: {
          color: 'var(--color-text)'
        },
        primary: {
          color: '{zinc.950}',
          inverseColor: '#ffffff',
          hoverColor: '{zinc.900}',
          activeColor: '{zinc.800}'
        },
        highlight: {
          background: 'var(--color-surface-hover)',
          focusBackground: 'var(--color-surface-hover)',
          color: 'var(--color-text)',
          focusColor: '#ffffff'
        }
      }
    }
  },
  components: {
    cascadedropdownsingle: CascadeDropdownSingleStyleConfig,
    paginator: PaginatorStyleConfig,
    textarea: {
      sm: {
        font: {
          size: '0.875rem'
        }
      }
    },
    tooltip: {
      background: 'var(--color-surface-tooltip) !important',
      color: 'var(--color-surface-default)',
      border: {
        radius: 'var(--radius-small)'
      },
      max: {
        width: '15rem'
      }
    },
    radiobutton: {
      width: '1rem',
      height: '1rem',
      icon: {
        size: '0.375rem'
      },
      checked: {
        focus: {
          border: {
            color: 'var(--color-interactive-hover)'
          }
        },
        hover: {
          background: 'var(--color-interactive-hover)',
          border: {
            color: 'var(--color-interactive-hover)'
          }
        },
        border: {
          color: 'var(--color-interactive)'
        },
        background: 'var(--color-interactive)'
      },
      border: {
        color: 'var(--color-text-subdued)'
      },
      focus: {
        ring: {
          color: 'var(--color-interactive)'
        }
      }
    },
    toast: {
      width: '300px',
      icon: {
        size: '1rem'
      },
      border: {
        width: '1px',
        radius: 'var(--border-radius-sm)'
      },
      colorScheme: {
        success: {
          color: 'var(--icon-success)',
          background: 'var(--color-surface-success)',
          border: {
            color: 'var(--border-color-success)'
          },
          detailColor: 'var(--icon-default)',
          shadow: '0px 4px 24px 0px rgba(12, 28, 25, 0.16)',
          closeButton: {
            hoverBackground: 'var(--color-transparent)',
            focusRing: 'none'
          }
        },
        info: {
          color: 'var(--icon-info)',
          background: 'var(--color-surface-highlight)',
          border: {
            color: 'var(--border-color-highlight)'
          },
          detailColor: 'var(--icon-default)',
          shadow: '0px 4px 24px 0px rgba(12, 28, 25, 0.16)',
          closeButton: {
            hoverBackground: 'var(--color-transparent)',
            focusRing: 'none'
          }
        },
        warn: {
          color: 'var(--icon-warning)',
          background: 'var(--color-surface-warning)',
          border: {
            color: 'var(--border-color-warning)'
          },
          detailColor: 'var(--icon-default)',
          shadow: '0px 4px 24px 0px rgba(12, 28, 25, 0.16)',
          closeButton: {
            hoverBackground: 'var(--color-transparent)',
            focusRing: 'none'
          }
        },
        error: {
          color: 'var(--icon-critical)',
          background: 'var(--color-surface-critical)',
          border: {
            color: 'var(--border-color-critical)'
          },
          detailColor: 'var(--icon-default)',
          shadow: '0px 4px 24px 0px rgba(12, 28, 25, 0.16)',
          closeButton: {
            hoverBackground: 'var(--color-transparent)',
            focusRing: 'none'
          }
        }
      },
      text: {
        gap: '0'
      },
      detail: {
        font: {
          weight: 400,
          size: '0.875rem'
        }
      },
      summary: {
        font: {
          weight: 700,
          size: '0.875rem'
        }
      },
      content: {
        padding: 'var(--padding-sm)',
        gap: '0.25rem'
      },
      closeButton: {
        width: '1.25rem',
        height: '1.25rem',
        borderRadius: '50%'
      }
    },
    iconfield: {
      icon: {
        color: 'var(--icon-default)'
      }
    },
    tag: {
      root: {
        fontSize: '0.875rem',
        fontWeight: '400',
        padding: '0.125rem 0.5rem',
        gap: '0.25rem',
        borderRadius: '{content.border.radius}',
        roundedBorderRadius: '{border.radius.xl}'
      },
      icon: {
        size: '0.75rem'
      },
      colorScheme: {
        light: {
          primary: {
            background: 'var(--color-tag-default)',
            color: 'var(--color-text)'
          },
          secondary: {
            background: '{surface.100}',
            color: '{surface.600}'
          },
          success: {
            background: '{green.100}',
            color: '{green.700}'
          },
          info: {
            background: '{sky.100}',
            color: '{sky.700}'
          },
          warn: {
            background: '{orange.100}',
            color: '{orange.700}'
          },
          danger: {
            background: '{red.100}',
            color: '{red.700}'
          },
          contrast: {
            background: '{surface.950}',
            color: '{surface.0}'
          }
        },
        dark: {
          primary: {
            background: 'color-mix(in srgb, {primary.500}, transparent 84%)',
            color: '{primary.300}'
          },
          secondary: {
            background: '{surface.800}',
            color: '{surface.300}'
          },
          success: {
            background: 'color-mix(in srgb, {green.500}, transparent 84%)',
            color: '{green.300}'
          },
          info: {
            background: 'color-mix(in srgb, {sky.500}, transparent 84%)',
            color: '{sky.300}'
          },
          warn: {
            background: 'color-mix(in srgb, {orange.500}, transparent 84%)',
            color: '{orange.300}'
          },
          danger: {
            background: 'color-mix(in srgb, {red.500}, transparent 84%)',
            color: '{red.300}'
          },
          contrast: {
            background: '{surface.0}',
            color: '{surface.950}'
          }
        }
      }
    },
    skeleton: {
      root: {
        borderRadius: 'var(--radius-default)'
      },
      colorScheme: {
        light: {
          root: {
            background: 'var(--color-surface-neutral)',
            animationBackground: 'rgba(255,255,255,0.4)'
          }
        }
      }
    },
    button: ButtonStyleConfig,
    selectbutton: SelectButtonStyleConfig,
    dialog: ModalStyleConfig,
    select: DropdownStyleConfig,
    datatable: TableStyleConfig,
    checkbox: CheckboxStyleConfig,
    tabs: TabStyleConfig,
    inputnumber: InputNumberStyleConfig,
    galleria: GalleriaStyleConfig,
    cascadeselect: CascadeDropdownSingleStyleConfig,
    inputtext: {
      fontSize: '14px'
    }
  }
})
